<script>
    import MenuBar from '../../components/MenuBar.svelte';

    import { getBalance } from 'domain/session/receivers.actions';
    import { IDLE, INCALL, OUTCALL, OUTGOING, RINGING } from 'common/callState';
    import { getReceivingLines } from 'domain/session/receivers.actions';
    import sessionStore from 'store/session';
    import pubsub from 'sweet-pubsub';
    import CallHistory from './CallHistory/index.svelte';
    import generateKeyPair, { decryptData } from 'services/encryption_keys';
    import ContactList from './ContactList.svelte';
    import ChatPage from '../Chat/Page.svelte';
    import DialNumber from './DialNumber.svelte';
    import Settings from './Settings.svelte';

    import { useQuery } from '@sveltestack/svelte-query';
    import {
        getCallLogAction,
        getVoiceMessageAction,
    } from 'domain/session/callLog.actions';
    import { getContactsAction } from 'domain/session/contacts.actions';
    import { getProfileAction } from 'domain/session/profile.actions';
    import { onMount } from 'svelte';
    import userStore from 'store/user';
    import { AmplitudeLogger, EVENTS, OpenObserveLogger } from '../../services/utils';

    let mode = 'dialScreen';
    const setMode = (m) => (mode = m);

    let dialNumber = '';

    let session = {
        callSession: null,
        peer: null,
        state: IDLE,
        ua: null,
        duration: 0,
        notes: '',
    };

    let lines = {}

    let balance, mobile, password, domain, host, port, protocol, username, sip_connected;
    let balance_list = ['0', '00'];

    let call_active = false;
    let take_note = false;
    let incoming_call_active = false;

    const ring = new Audio('https://outlinks.pressone.co/p0p');

    let receiverID, mobileID;

    userStore.subscribe((value) => {
        receiverID = value.receiverID;
        mobileID = value.mobileID;
        mobile = value.mobile;
    });

    const setCallActive = (active) => {
        call_active = active;
    };

    const setTakeNote = (active) => {
        take_note = active;
    };

    const setDuration = (duration) => {
        session = {
            ...session,
            duration,
        };
    };

    const parseBalance = (num) => {
        let integerPart = Math.floor(num).toString();
        let decimalPart = (num - Math.floor(num)).toFixed(2).split('.')[1];
        return [integerPart, decimalPart];
    };

    const setNotes = (notes) => {
        if (session.state === INCALL || session.state === OUTCALL) {
            session = {
                ...session,
                notes,
            };
        } else {
        }
    };

    $: {
        if (session.state === INCALL || session.state === OUTCALL) {
            incoming_call_active = false;
            call_active = true;
        }
    }

    let remoteAudio;

    sessionStore.subscribe((value) => {
        balance = value.balance;

        try {
            balance_list = parseBalance(balance);
        } catch (error) {
            // console.log(error);
        }
    });

    generateKeyPair().then(async (keyPair) => {
        let publicKey = keyPair.publicKey;
        let privateKey = keyPair.privateKey;

        let response = await getReceivingLines(publicKey, receiverID);

        password = await decryptData(privateKey, response.password);

        domain = response.domain;
        host = response.host;
        port = response.port;
        protocol = response.protocol;
        username = response.username;

        lines = {
            ...response,
            password
        }

        setUp();
    });

    const resetSession = () => {
        session = {
            ...session,
            callSession: null,
            peer: null,
            state: IDLE,
            duration: 0,
            notes: '',
        };
        incoming_call_active = false;
    };

    const setUp = () => {
        const socket = new JsSIP.WebSocketInterface(
            `${protocol}://${host}:${port}/${protocol}`
        );

        socket['via_transport'] = 'WSS';

        const configuration = {
            sockets: [socket],
            uri: `sip:${username}@${domain}`,
            password: password,
            session_timers: true,
            use_preloaded_route: false,
        };

        const ua = new JsSIP.UA(configuration);

        ua.on('connected', () => {
            sip_connected = true;
        });

        ua.on('disconnected', () => {
            sip_connected = false;
        });

        ua.on('newRTCSession', (data) => {
            const new_session = data.session;

            new_session.on('ended', async () => {
                // when the call is ended
                session = {
                    ...session,
                    state: IDLE,
                };

                let session_direction = new_session.direction;
                let duration = session.duration;

                let direction = session_direction === 'incoming' ? 'Inbound' : 'Outbound';

                pubsub.emit('call_ended', {
                    direction,
                    duration,
                    peer: session.peer,
                    notes: session.notes,
                });

                AmplitudeLogger.log(EVENTS.CALL_ENDED, {
                    phone: session.peer,
                    duration,
                    direction
                })
            });

            new_session.on('failed', async () => {
                // when the call is rejected by the other party or missed call
                if (session.isTransfer) {
                    session.isTransfer = false
                    session.transferStatus = "Call transfer failed!"
                    setTimeout(() => {
                        session.transferStatus = null
                    }, 2000)
                    return;
                }

                let session_direction = new_session.direction;
                let duration = session.duration;

                let direction =
                    duration === 0 && session_direction === 'incoming'
                        ? 'Missed'
                        : session_direction === 'incoming'
                        ? 'Inbound'
                        : 'Outbound';

                if (direction === 'Missed') {
                    pubsub.emit('call_missed', {
                        direction,
                        duration,
                        peer: session.peer,
                        notes: session.notes,
                    });
                } else {
                    pubsub.emit('call_ended', {
                        direction,
                        duration,
                        peer: session.peer,
                        notes: session.notes,
                    });
                }

                resetSession();
                
                setCallActive(false);
                AmplitudeLogger.log(EVENTS.CALL_FAILED, {
                    phone: session.peer,
                    duration,
                    direction
                })
            });

            new_session.on('confirmed', () => {
                session = {
                    ...session,
                    state: new_session.direction === 'incoming' ? INCALL : OUTCALL,
                };

                pubsub.emit('call_started', {
                    direction:
                        new_session.direction === 'incoming' ? 'Inbound' : 'Outbound',
                    peer: session.peer,
                });
            });

            const peer =
                new_session.remote_identity.display_name ||
                new_session.remote_identity._uri._user;

            if (session.state === IDLE) {
                session = {
                    ...session,
                    callSession: new_session,
                    peer,
                    state: new_session.direction === 'incoming' ? RINGING : OUTGOING,
                    duration: 0,
                    notes: '',
                };

                if (new_session.direction === 'incoming') {
                    incoming_call_active = true;
                    pubsub.emit('call_ringing', {
                        peer,
                    });
                } else {
                    console.log('OUTGOING_CALL')
                }
            }

            switch (new_session.direction) {
                case 'outgoing':
                    new_session.connection.addEventListener('addstream', (e) => {
                        remoteAudio.srcObject = e.stream;
                        remoteAudio.play();
                    });
                    break;

                case 'incoming':
                    new_session.on('peerconnection', (data) => {
                        data.peerconnection.addEventListener('addstream', (e) => {
                            remoteAudio.srcObject = e.stream;
                            remoteAudio.play();
                        });
                    });

                    break;
            }
            
            if (
                new_session.direction === 'incoming' &&
                (session.state === IDLE || session.state === RINGING)
            ) {
                if (mode !== 'dialScreen') {
                    mode = 'dialScreen';
                }
                incoming_call_active = true;
            }
            
        });

        ua.on('registrationFailed', async ({ cause, response }) => {
            const { status_code: status = null, body = "" } = response || {};
            const options = {
                cause,
                status,
                body,
                extension: username
            }
            
            OpenObserveLogger.log(EVENTS.USER_REGISTRATION_FAILURE, options)
        });

        const initialRegisterFunction = ua._registrator.register

        ua._registrator.register = () => {
            const options = {
                extension: username
            }
            
            OpenObserveLogger.log(EVENTS.USER_REGISTRATION, options)
            initialRegisterFunction.call(ua.registrator())
        }

        ua.start();

        session = {
            ...session,
            ua,
        };
    };

    const answer = () => {
        const options = {
            mediaConstraints: { audio: true, video: false },
            pcConfig: {
                rtcpMuxPolicy: 'negotiate',
                iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
            },
        };
        session.callSession.answer(options);
        session = {
            ...session,
            state: INCALL,
        };
    };

    $: {
        if (
            session.state === INCALL ||
            session.state === OUTCALL ||
            session.state === IDLE
        ) {
            if (!ring.paused) {
                ring.pause();
                ring.currentTime = 0;
            }
        }
    }

    onMount(async () => {
        const [profile, contacts] = await Promise.all([
            getProfileAction(),
            getContactsAction(mobileID),
            getCallLogAction(mobileID),
            getVoiceMessageAction(mobileID),
        ]);

        pubsub.on('dial', (number) => {
            mode = 'dialScreen';
            dialNumber = number;
        });
    });

    const QBalance = useQuery('balance', getBalance, {
        refetchInterval: 2 * 1000 * 60, // update every 2 minutes
    });
    $QBalance; // init the query. This is a hack to get the query to run on mount

    const QCallLog = useQuery('callLog', () => getCallLogAction(mobileID), {
        refetchInterval: 2 * 1000 * 60, // update every 2 minutes
    });
    $QCallLog; // init the query. This is a hack to get the query to run on mount
</script>

<div id="user-settings-tabs">
    <div class="tabs">
        {#if mode == 'dialScreen'}
            <DialNumber
                input={dialNumber}
                bind:remoteAudio
                bind:lines={lines}
                {answer}
                {balance_list}
                {call_active}
                {incoming_call_active}
                {mobile}
                {ring}
                bind:session={session}
                {sip_connected}
                {take_note}
                {setCallActive}
                {setDuration}
                {setNotes}
                {setTakeNote}
            />
        {:else if mode == 'callLogs'}
            <CallHistory />
        {:else if mode == 'contactList'}
            <ContactList />
        {:else if mode == 'messages'}
            <ChatPage />
        {:else if mode == 'userSettings'}
            <Settings />
        {/if}
    </div>

    <div class="menu">
        {#if mode !== 'messages'}
        <MenuBar {mode} {setMode} />
        {/if}
    </div>
</div>

<style lang="scss">
    #user-settings-tabs {
        display: flex;
        flex-direction: column;
        flex: 1;
    }
    .menu {
        position: absolute;
        bottom: 0;
        width: 100%;
    }
</style>